import { useCallback, useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { ScanLine, LogOut, RotateCw } from "lucide-react";
import Brand from "@/components/common/Brand";
import { RECEPCION } from "@/constants/testIds";
import {
  getReceptionSession,
  logoutRecepcion,
  verificarSesionRecepcion,
} from "@/data/receptionAuthStore";

/**
 * Layout del Portal de Recepción · el acceso se concede sólo tras
 * confirmar la sesión con el servidor (`/auth/me`).
 */
export default function ReceptionLayout() {
  const navigate = useNavigate();
  const [verif, setVerif] = useState({ estado: "verificando" });

  const verificar = useCallback(async () => {
    if (!getReceptionSession()) {
      navigate("/recepcion/login", { replace: true });
      return;
    }
    setVerif({ estado: "verificando" });
    const r = await verificarSesionRecepcion();
    if (r.ok) return setVerif({ estado: "ok", session: r.sesion });
    if (r.tipo === "sesion" || r.tipo === "permiso") {
      const cat = r.categoria ? `&cat=${encodeURIComponent(r.categoria)}` : "";
      navigate(`/recepcion/login?aviso=sesion-no-reconocida${cat}`, { replace: true });
      return;
    }
    setVerif({ estado: "error", status: r.status });
  }, [navigate]);

  useEffect(() => {
    verificar();
  }, [verificar]);

  const salir = () => {
    logoutRecepcion();
    navigate("/recepcion/login", { replace: true });
  };

  if (verif.estado === "verificando") {
    return (
      <p data-testid={RECEPCION.layoutVerificando} className="p-10 text-muted-foreground animate-pulse">
        Verificando sesión…
      </p>
    );
  }
  if (verif.estado === "error") {
    return (
      <div data-testid={RECEPCION.layoutErrorVerificacion} role="alert" className="p-10 flex flex-col items-start gap-4">
        <p className="text-foreground">
          No se pudo verificar la sesión con el servidor ({verif.status ? `HTTP ${verif.status}` : "sin respuesta"}).
        </p>
        <button
          type="button"
          data-testid={RECEPCION.layoutReintentar}
          onClick={verificar}
          className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm text-primary-foreground hover:opacity-90 transition-opacity"
        >
          <RotateCw className="w-4 h-4" strokeWidth={1.6} /> Reintentar
        </button>
      </div>
    );
  }
  const session = verif.session;
  return (
    <div
      data-testid={RECEPCION.layout}
      className="relative min-h-[100dvh] bg-background text-foreground flex flex-col"
    >
      <header className="sticky top-0 z-40 w-full glass border-b hairline">
        <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 md:px-10 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Brand />
            <span
              data-testid={RECEPCION.brand}
              className="hidden sm:inline-flex items-center gap-2 rounded-full border hairline px-3 py-1 text-xs text-muted-foreground"
            >
              <ScanLine className="w-3.5 h-3.5 text-accent" strokeWidth={1.8} />
              <span className="overline text-accent">Recepción</span>
              <span className="text-muted-foreground">·</span>
              <span className="text-foreground font-medium">
                {session.nombre || session.usuario}
              </span>
            </span>
          </div>
          <button
            type="button"
            onClick={salir}
            data-testid={RECEPCION.linkSalir}
            className="inline-flex items-center justify-center gap-2 rounded-full px-4 min-h-[44px] text-xs font-medium border hairline bg-card hover:bg-muted transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" strokeWidth={1.8} />
            Cerrar sesión
          </button>
        </div>
      </header>
      <main className="flex-1 w-full max-w-6xl mx-auto px-4 sm:px-6 md:px-10 py-6 md:py-10">
        <Outlet />
      </main>
      <footer className="w-full border-t hairline">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-10 py-4 flex items-center justify-between">
          <p className="text-xs text-muted-foreground">
            © Casino Tampiqueño · CT Connect
          </p>
          <p className="overline text-muted-foreground">
            Portal de recepción · {session.rol}
          </p>
        </div>
      </footer>
    </div>
  );
}
